import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { jobsSocket, type WsMessage } from '../lib/ws';
import type { AiJobSummary, Job } from '../types';

// Trigger threshold for the post-import auto-organize toast (MF-68). Below this
// the per-cluster LLM cost isn't worth the prompt — users can still use
// /ai-organize manually.
const AUTO_ORGANIZE_MIN_BOOKMARKS = 20;

export type UnifiedJobKind = 'regular' | 'ai';

export interface UnifiedJob {
  kind: UnifiedJobKind;
  id: string;
  status: string;
  type: string;                 // 'bulk_add'|'import'|'meta_refetch' | AI scope label
  progress?: number;
  total?: number;
  createdAt: string;
  updatedAt: string;
  error?: string | null;
  raw: Job | AiJobSummary;
}

interface JobsContextValue {
  jobs: UnifiedJob[];
  activeJobs: UnifiedJob[];
  refresh: () => Promise<void>;
  onBookmarksChanged(listener: () => void): () => void;
}

const JobsContext = createContext<JobsContextValue | null>(null);

const BOOKMARK_REFRESH_TYPES = new Set(['import', 'meta_refetch', 'bulk_add']);

function toUnifiedFromJob(j: Job): UnifiedJob {
  return {
    kind: 'regular',
    id: j.id,
    status: j.status,
    type: j.type,
    progress: j.progress,
    total: j.total,
    createdAt: j.createdAt,
    updatedAt: j.updatedAt,
    error: j.error,
    raw: j,
  };
}

function toUnifiedFromAi(a: AiJobSummary): UnifiedJob {
  let typeLabel: string;
  switch (a.scope.type) {
    case 'unsorted': typeLabel = 'ai_unsorted'; break;
    case 'bookmark_ids': typeLabel = 'ai_import'; break;
    default: typeLabel = 'ai_all';
  }
  return {
    kind: 'ai',
    id: a.id,
    status: a.status,
    type: typeLabel,
    progress: undefined,
    total: undefined,
    createdAt: a.createdAt,
    updatedAt: a.readyAt ?? a.appliedAt ?? a.createdAt,
    raw: a,
  };
}

interface JobsProviderProps {
  enabled: boolean;
  children: ReactNode;
}

export function JobsProvider({ enabled, children }: JobsProviderProps) {
  const [map, setMap] = useState<Record<string, UnifiedJob>>({});
  const bookmarkListeners = useRef(new Set<() => void>());
  // Dedup post-import auto-organize prompts: server may broadcast the terminal
  // job_update more than once (rehydrate, retry handler), but the toast must
  // only appear the first time per job-id within a session.
  const promptedImportIds = useRef(new Set<string>());
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  // Keep latest t/i18n/navigate in refs so the WS listener can pick them up
  // without resubscribing on every render.
  const tRef = useRef(t);
  const i18nRef = useRef(i18n);
  const navigateRef = useRef(navigate);
  tRef.current = t;
  i18nRef.current = i18n;
  navigateRef.current = navigate;

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const [jobsRes, aiRes] = await Promise.all([
        api.jobs.list({ limit: 50 }),
        api.listAiJobs().catch(() => [] as AiJobSummary[]),
      ]);
      setMap((prev) => {
        const next = { ...prev };
        for (const j of jobsRes.items) next[`regular:${j.id}`] = toUnifiedFromJob(j);
        for (const a of aiRes) next[`ai:${a.id}`] = toUnifiedFromAi(a);
        return next;
      });
    } catch {
      // transient; next WS message or next refresh will catch up
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      jobsSocket.stop();
      setMap({});
      return;
    }
    refresh();
    jobsSocket.start();
    const unsubscribe = jobsSocket.subscribe((msg: WsMessage) => {
      if (msg.kind === 'job_update') {
        const payload = msg.payload as Job;
        setMap((prev) => ({ ...prev, [`regular:${payload.id}`]: toUnifiedFromJob(payload) }));
        if (
          (payload.status === 'done' || payload.status === 'failed' || payload.status === 'cancelled') &&
          BOOKMARK_REFRESH_TYPES.has(payload.type)
        ) {
          for (const fn of bookmarkListeners.current) fn();
        }
        // MF-68: post-import auto-organize CTA. Dedup per-jobId so retries /
        // duplicate broadcasts don't re-toast.
        if (
          payload.type === 'import' &&
          payload.status === 'done' &&
          !promptedImportIds.current.has(payload.id)
        ) {
          const ids = Array.isArray(payload.result?.unsortedBookmarkIds)
            ? (payload.result!.unsortedBookmarkIds as unknown[]).filter(
                (x): x is string => typeof x === 'string',
              )
            : [];
          if (ids.length >= AUTO_ORGANIZE_MIN_BOOKMARKS) {
            promptedImportIds.current.add(payload.id);
            toast(tRef.current('toasts.autoOrganizePrompt', { count: ids.length }), {
              action: {
                label: tRef.current('toasts.autoOrganizeCta'),
                onClick: async () => {
                  try {
                    // MF-186 — carry the import target Space through to AI
                    // Organize so the clusterer doesn't pull cross-Space
                    // centroids when seeding existing folders.
                    const importSpaceId =
                      typeof payload.input?.spaceId === 'string'
                        ? (payload.input.spaceId as string)
                        : undefined;
                    const job = await api.startAiOrganize(
                      { type: 'bookmark_ids', ids },
                      i18nRef.current.language || 'en',
                      { useExistingFolders: true, postPassAttach: true },
                      importSpaceId,
                    );
                    navigateRef.current(`/ai-organize/${job.id}`);
                  } catch (err) {
                    if (err instanceof ApiError && err.code === 'insufficient_tokens') {
                      toast.error(tRef.current('toasts.insufficientTokens'), {
                        action: {
                          label: tRef.current('aiOrganize.estimate.topUpCta'),
                          onClick: () => navigateRef.current('/settings/balance'),
                        },
                      });
                    } else {
                      toast.error(tRef.current('aiOrganize.startFailed'));
                    }
                  }
                },
              },
              duration: 12_000,
            });
          }
        }
      } else if (msg.kind === 'ai_job_update') {
        const payload = msg.payload as Partial<AiJobSummary> & { id: string };
        setMap((prev) => {
          const key = `ai:${payload.id}`;
          const existing = prev[key];
          if (!existing) {
            // We don't have the full summary; refetch lazily.
            refresh();
            return prev;
          }
          const merged = { ...(existing.raw as AiJobSummary), ...payload };
          return { ...prev, [key]: toUnifiedFromAi(merged as AiJobSummary) };
        });
      } else if (
        msg.kind === 'bookmark_meta_update' ||
        msg.kind === 'bookmark_created' ||
        msg.kind === 'bookmark_updated' ||
        msg.kind === 'bookmark_deleted' ||
        msg.kind === 'bookmarks_bulk_deleted'
      ) {
        for (const fn of bookmarkListeners.current) fn();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [enabled, refresh]);

  const onBookmarksChanged = useCallback((listener: () => void): (() => void) => {
    bookmarkListeners.current.add(listener);
    return () => {
      bookmarkListeners.current.delete(listener);
    };
  }, []);

  const jobs = useMemo(
    () =>
      Object.values(map).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [map],
  );

  const activeJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === 'pending' ||
          j.status === 'running' ||
          j.status === 'ready',
      ),
    [jobs],
  );

  const value = useMemo<JobsContextValue>(
    () => ({ jobs, activeJobs, refresh, onBookmarksChanged }),
    [jobs, activeJobs, refresh, onBookmarksChanged],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

export function useJobs(): JobsContextValue {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error('useJobs must be used within a JobsProvider');
  return ctx;
}

// Side-effect-only subscriber: registers the given refresh callback with the
// jobs context so that whenever a bookmark-touching job terminates, caller
// bookmarks/folders/tags fetchers re-run. Designed for App.tsx to use inside
// the provider tree without restructuring the main render.
export function JobsBookmarkBridge({ onRefresh }: { onRefresh: () => void }) {
  const { onBookmarksChanged } = useJobs();
  useEffect(() => onBookmarksChanged(onRefresh), [onBookmarksChanged, onRefresh]);
  return null;
}
